import React, { createContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import useAxios from "../../hooks/useAxios";
import useAxiosPagination from "../../hooks/useAxiosPagination";
import { useMediaQuery } from "react-responsive";
import { Sizes } from "../../styles";

//helpers and hooks
import { FormatFiltersQuery } from "./helpers";

const Context = createContext();

//Filters API:
//https://cne.local/wp-json/cne/v1/careers

export const AppProvider = ({ children }) => {
	const [selectedFilters, setSelectedFilters] = useState([]);
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [jobItemsUrl, setJobItemsUrl] = useState(`careers?page=1`);
	const [loading, setLoading] = useState(true);
	const { response: jobItems, totalPages } = useAxiosPagination(jobItemsUrl);
	const featuredJobItems = useAxios("featured-careers");

	const [isFiltersMobileModalOpen, setIsFiltersMobileModalOpen] =
		useState(false);

	// hard coded filters
	const careerFilters = {
		sort: {
			title: "Sort By",
			filters: [
				{ name: "Latest Published", slug: "asc" },
				{ name: "Earliest Published", slug: "desc" },
			],
		},
		commitment: {
			title: "By Commitment",
			filters: [
				{ name: "Full time", slug: "full-time" },
				{ name: "Part time", slug: "part-time" },
				{ name: "Seasonal", slug: "seasonal" },
				{ name: "Other", slug: "other"}
				// Add other commitment filters with slugs
			],
		},
		category: {
			title: "By Category",
			filters: [
				{ name: "Business Services", slug: "business-services" },
				{ name: "CNE Foundation", slug: "cne-foundation" },
				{ name: "CNEF", slug: "cnef" },
				{ name: "Event Services & Operations", slug: "event-services-amp-operations" },
				{ name: "Exhibit Sales & Sponsorship", slug: "exhibit-sales-amp-sponsorship" },
				{ name: "Programs", slug: "programs" },
				{ name: "Other", slug: "other" }
				// Add other category filters with slugs
			],
		},
	};

	const isMediumOrLargeScreen = useMediaQuery({
		query: `(min-width: ${Sizes.screens.medium})`,
	});

	// onClick handler for selecting and deselecting a filter
	const handleFilterSelection = (
		prevSelectedFilters,
		categorySlug,
		filterValue
	) => {
		const existingFilterIndex = prevSelectedFilters.findIndex(
			(filter) =>
				filter.category === categorySlug && filter.value === filterValue.slug
		);

		if (existingFilterIndex >= 0) {
			// If the clicked filter is already selected, we deselect it
			const updatedFilters = [...prevSelectedFilters];
			updatedFilters.splice(existingFilterIndex, 1);
			return updatedFilters;
		} else {
			// If a filter from this category is already selected, we remove it
			const filtersWithoutCategory = prevSelectedFilters.filter(
				(filter) => filter.category !== categorySlug
			);
			return [
				...filtersWithoutCategory,
				{ category: categorySlug, value: filterValue.slug },
			];
		}
	};

	const handleSelectFilter = (categorySlug, filterValue) => {
		setSelectedFilters((prevSelectedFilters) =>
			handleFilterSelection(prevSelectedFilters, categorySlug, filterValue)
		);
		scrollToTop();
		setCurrentPageNumber(1); // Reset the page number to 1 when a filter is selected
	};

	// onClick handler for selecting and deselecting a filter
	const clearAllFilters = () => {
		setSelectedFilters([]);
		setCurrentPageNumber(1);
		scrollToTop();
	};

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true); // Set loading to true before fetching data
			await setJobItemsUrl(
				`careers?page=${currentPageNumber}${FormatFiltersQuery(
					selectedFilters
				)}`
			);
			setLoading(false); // Set loading to false after data is fetched
		};

		fetchData();
	}, [selectedFilters, currentPageNumber]);

	// handles the scroll behavior on open and closing of the Filters modal on mobile
	useEffect(() => {
		if (!isMediumOrLargeScreen) {
			if (isFiltersMobileModalOpen) {
				document.body.style.overflow = "hidden";
			} else {
				document.body.style.overflow = "scroll";
			}
		}
	}, [isFiltersMobileModalOpen, isMediumOrLargeScreen]);
	

	const scrollToTop = () => {
		// need a setTimeout for it to work on Safari and mobile devices
		return setTimeout(() => {
			window.scroll({ top: 500, left: 0, behavior: "smooth" });
		}, 10);
	};

	// Pagination helpers actions
	const handleIncrementPage = () => {
		if (currentPageNumber < totalPages.length) {
			setCurrentPageNumber((currentNumber) => currentNumber + 1);
			scrollToTop();
		}
	};

	const handleDecrementPage = () => {
		if (currentPageNumber > 1) {
			setCurrentPageNumber((currentNumber) => currentNumber - 1);
			scrollToTop();
		}
	};

	const handleButtonPageSelect = (buttonNumber) => {
		setCurrentPageNumber(buttonNumber);
		scrollToTop();
	};

	const openModal = () => {
		setIsFiltersMobileModalOpen(true);
	};

	const closeModal = (e) => {
		e.stopPropagation();
		setIsFiltersMobileModalOpen(false);
	};

	const data = {
		selectedFilters,
		jobItems,
		featuredJobItems,
		totalPages,
		currentPageNumber,
		careerFilters,
	};

	const actions = {
		handleSelectFilter,
		handleIncrementPage,
		handleDecrementPage,
		handleButtonPageSelect,
		clearAllFilters,
	};

	const modalData = {
		isMediumOrLargeScreen,
		isFiltersMobileModalOpen,
	};

	const modalActions = {
		openModal,
		closeModal,
	};

	return (
		<Context.Provider
			value={{ actions, data, modalData, modalActions, loading }}
		>
			{children}
		</Context.Provider>
	);
};

AppProvider.propTypes = {
	children: PropTypes.node,
};
export default Context;
